'use client';

import { fetchGoodsList } from '@/api/product';
import type { IProductListApiResponse } from '@/api/types/product';
import { env } from '@/utils/env';
import { buildBestsellerProductListParams, mapRuntimeBestsellerProduct } from './runtimeData';
import type { BestsellerProduct, BestsellerVibe } from './types';
import { VIBE_LOAD_BATCH_SIZE } from './vibeCarousel';

export interface VibeProductPage {
  products: BestsellerProduct[];
  hasMore: boolean;
  total?: number;
}

interface ProductListResponse {
  code: number;
  data?: IProductListApiResponse;
}

export async function loadVibeProductPage(
  vibe: BestsellerVibe,
  page: number,
): Promise<VibeProductPage> {
  if (!vibe.productGroupId) {
    return { products: [], hasMore: false, total: 0 };
  }

  const params = buildBestsellerProductListParams(
    vibe.productGroupId,
    vibe.categoryKey && vibe.attributeValue
      ? { categoryKey: vibe.categoryKey, attributeValue: vibe.attributeValue }
      : undefined,
    VIBE_LOAD_BATCH_SIZE,
    vibe.stockSortFirst,
    vibe.inStockFilter,
  );
  const response = (await fetchGoodsList({ ...params, page })) as unknown as ProductListResponse;
  if (response.code !== 200 || !response.data) {
    throw new Error(`Failed to load bestseller vibe page ${page}`);
  }

  const list = response.data.list || [];
  const products = list.map((product, index) =>
    mapRuntimeBestsellerProduct(
      product as unknown as Record<string, unknown>,
      (page - 1) * VIBE_LOAD_BATCH_SIZE + index + 1,
      env.NEXT_PUBLIC_S3_URL,
    ),
  );
  const responseTotal = Number(response.data.total ?? response.data.all_total);
  const total = Number.isFinite(responseTotal)
    ? Math.max(responseTotal, products.length)
    : undefined;

  return {
    products,
    total,
    hasMore:
      total !== undefined && total > 0
        ? page * VIBE_LOAD_BATCH_SIZE < total
        : list.length >= VIBE_LOAD_BATCH_SIZE,
  };
}
